/**
 * เรนเดอร์สลิปลง canvas ก่อนส่งไปพิมพ์
 *
 * เหตุผลที่พิมพ์เป็นภาพ ไม่ใช่ส่งข้อความให้เครื่องพิมพ์จัดหน้าเอง
 *   · ESC/POS แต่ละยี่ห้อรองรับ codepage ไทยไม่เหมือนกัน สระลอยผิดที่กันคนละแบบ
 *   · เห็นบนจอเป็นยังไง ออกกระดาษเป็นอย่างนั้นเป๊ะ ตรวจสอบก่อนพิมพ์ได้
 *   · วาง QR พร้อมเพย์ลงในสลิปใบเดียวกันได้เลย
 *
 * ค่าฟอนต์ทุกตัวมาจาก docs/font-spec.md ซึ่งวัดจากการทดลองพิมพ์จริง
 * ★ ถ้าเปลี่ยนขนาดฟอนต์ ต้องคำนวณความสูงบรรทัดใหม่ด้วยเสมอ ★
 */

import { baht } from './format.ts';
import { encodeQr } from './qr.ts';
import { promptPayPayload } from './promptpay.ts';

const FONT = '"Bai Jamjuree","Noto Sans Thai",sans-serif';

/** น้ำหนัก 600 ขึ้นไปเท่านั้นบนสลิป — ที่ 400 วรรณยุกต์หายตอนพิมพ์ขาวดำ */
const ITEM_FONT = `600 24px ${FONT}`;
const HEAD_FONT = `700 26px ${FONT}`;
const META_FONT = `600 22px ${FONT}`;
const SMALL_FONT = `500 20px ${FONT}`;
const TOTAL_FONT = `700 30px ${FONT}`;

/** ความสูงบรรทัดของรายการสินค้า — วัดมาแล้วว่า 34 ไม่พอ วรรณยุกต์ชนกัน */
const ITEM_LINE = 46;

const PAD = 16;

/** ความกว้างเป็นจุด — 80 มม. = 576 จุด · 58 มม. = 384 จุด */
export function dotsFor(paperWidthMm: number): number {
  return paperWidthMm === 58 ? 384 : 576;
}

/**
 * สระบนล่างและวรรณยุกต์ไทย — อักขระที่ลอยอยู่บนตัวอักษรอื่น ไม่มีความกว้างของตัวเอง
 * ★ ตัดบรรทัดคั่นระหว่างตัวอักษรกับสระของมันไม่ได้เด็ดขาด ★
 * "ปุระ" ที่ถูกตัดผิดจะกลายเป็น "ป" ท้ายบรรทัดกับ "ุระ" ขึ้นต้นบรรทัดใหม่
 */
const COMBINING = /[ัิ-ฺ็-๎]/;

/** แยกข้อความเป็นกลุ่มที่ตัดกลางไม่ได้ — ตัวอักษรหนึ่งตัวพร้อมสระและวรรณยุกต์ของมัน */
function clusters(text: string): string[] {
  const out: string[] = [];
  for (const ch of text) {
    if (out.length > 0 && COMBINING.test(ch)) out[out.length - 1] += ch;
    else out.push(ch);
  }
  return out;
}

/**
 * ตัดบรรทัดภาษาไทย — วัดทีละกลุ่มอักขระ
 *
 * ★ ภาษาไทยไม่มีช่องว่างระหว่างคำ split(' ') จึงใช้ไม่ได้ ★
 * บั๊กเดิมทำให้ชื่อเมนูยาว ๆ ล้นออกนอกกระดาษ และจำนวนหลุดไปคนละบรรทัดกับชื่อ
 *
 * ตัดกลางคำได้ (ยอมรับได้ เพราะไม่มีพจนานุกรมมาบอกขอบเขตคำ)
 * แต่ตัดกลางตัวอักษรกับสระของมันไม่ได้
 *
 * แยกออกมาเป็นฟังก์ชันที่รับตัววัดความกว้างเข้ามา เพื่อให้ทดสอบได้โดยไม่ต้องมี canvas
 */
export function wrapText(
  measure: (text: string) => number,
  text: string,
  maxWidth: number,
): string[] {
  if (text === '') return [''];

  const lines: string[] = [];
  let current = '';

  for (const cluster of clusters(text)) {
    if (cluster === '\n') {
      lines.push(current);
      current = '';
      continue;
    }
    if (current !== '' && measure(current + cluster) > maxWidth) {
      lines.push(current);
      current = cluster;
    } else {
      current += cluster;
    }
  }
  if (current !== '') lines.push(current);
  return lines;
}

/* ══ ตัวช่วยวาด ═══════════════════════════════════════════════
   สะสมคำสั่งวาดไว้ก่อนพร้อมนับความสูง แล้วค่อยสร้าง canvas
   ขนาดพอดีทีเดียว — กระดาษความร้อนไม่มี "หน้า" ความสูงจึงต้องพอดี
   ไม่งั้นจะได้กระดาษเปล่าต่อท้ายทุกใบ                            */

type DrawOp = (ctx: CanvasRenderingContext2D) => void;

class Slip {
  private ops: DrawOp[] = [];
  private measurer: CanvasRenderingContext2D;
  y = PAD;

  constructor(readonly width: number) {
    this.measurer = document.createElement('canvas').getContext('2d')!;
  }

  private measure(text: string, font: string): number {
    this.measurer.font = font;
    return this.measurer.measureText(text).width;
  }

  private draw(op: DrawOp): void {
    this.ops.push(op);
  }

  gap(px: number): this {
    this.y += px;
    return this;
  }

  text(
    content: string,
    options: { font?: string; align?: CanvasTextAlign; lineHeight?: number } = {},
  ): this {
    const font = options.font ?? ITEM_FONT;
    const align = options.align ?? 'left';
    const lineHeight = options.lineHeight ?? ITEM_LINE;
    const lines = wrapText((t) => this.measure(t, font), content, this.width - PAD * 2);

    for (const line of lines) {
      const y = this.y + lineHeight * 0.7;
      const x = align === 'center' ? this.width / 2 : align === 'right' ? this.width - PAD : PAD;
      this.draw((ctx) => {
        ctx.font = font;
        ctx.textAlign = align;
        ctx.fillText(line, x, y);
      });
      this.y += lineHeight;
    }
    return this;
  }

  /** บรรทัดที่มีข้อความซ้ายและตัวเลขขวา เช่น ยอดรวม / ส่วนลด */
  pair(label: string, value: string, options: { font?: string; lineHeight?: number } = {}): this {
    const font = options.font ?? ITEM_FONT;
    const lineHeight = options.lineHeight ?? 34;
    const y = this.y + lineHeight * 0.72;
    this.draw((ctx) => {
      ctx.font = font;
      ctx.textAlign = 'left';
      ctx.fillText(label, PAD, y);
      ctx.textAlign = 'right';
      ctx.fillText(value, this.width - PAD, y);
    });
    this.y += lineHeight;
    return this;
  }

  rule(style: 'dashed' | 'solid' = 'dashed'): this {
    const y = this.y + 6;
    this.draw((ctx) => {
      ctx.beginPath();
      ctx.setLineDash(style === 'dashed' ? [4, 4] : []);
      ctx.moveTo(PAD, y);
      ctx.lineTo(this.width - PAD, y);
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#000';
      ctx.stroke();
      ctx.setLineDash([]);
    });
    this.y += 13;
    return this;
  }

  /**
   * รายการสินค้า — จำนวนอยู่ซ้าย ชื่ออยู่กลาง ราคาอยู่ขวา
   * ★ จำนวนและราคาตรึงกับบรรทัดแรกของชื่อเสมอ ★ ชื่อจะยาวกี่บรรทัดก็ตาม
   */
  item(qty: number, name: string, note: string, amount: string | null): this {
    const qtyWidth = 54;
    const amountWidth = amount === null ? 0 : 108;
    const nameWidth = this.width - PAD * 2 - qtyWidth - amountWidth;

    const lines = wrapText((t) => this.measure(t, ITEM_FONT), name, nameWidth);
    const noteLines = note
      ? wrapText((t) => this.measure(t, SMALL_FONT), note, nameWidth)
      : [];

    const first = this.y + ITEM_LINE * 0.7;
    const nameX = PAD + qtyWidth;

    this.draw((ctx) => {
      ctx.font = ITEM_FONT;
      ctx.textAlign = 'left';
      ctx.fillText(`${qty}`, PAD, first);
      lines.forEach((line, i) => {
        ctx.font = ITEM_FONT;
        ctx.fillText(line, nameX, first + i * ITEM_LINE);
      });
      noteLines.forEach((line, i) => {
        ctx.font = SMALL_FONT;
        ctx.fillText(line, nameX, first + (lines.length + i) * ITEM_LINE - 8);
      });
      if (amount !== null) {
        ctx.font = ITEM_FONT;
        ctx.textAlign = 'right';
        ctx.fillText(amount, this.width - PAD, first);
      }
    });

    this.y += (lines.length + noteLines.length) * ITEM_LINE;
    return this;
  }

  /** QR ขาวดำล้วน — วาดทีละโมดูลเป็นสี่เหลี่ยม ไม่ผ่านการย่อขยายที่ทำให้ขอบเบลอ */
  qr(text: string, targetSize: number): this {
    const matrix = encodeQr(text);
    const quiet = 4;
    // ปัดขนาดโมดูลเป็นจำนวนเต็มพิกเซล — ขอบที่ตกเศษทำให้เครื่องสแกนอ่านพลาด
    const scale = Math.max(2, Math.floor(targetSize / (matrix.size + quiet * 2)));
    const side = (matrix.size + quiet * 2) * scale;
    const left = Math.floor((this.width - side) / 2);
    const top = this.y;

    this.draw((ctx) => {
      ctx.fillStyle = '#fff';
      ctx.fillRect(left, top, side, side);
      ctx.fillStyle = '#000';
      for (let y = 0; y < matrix.size; y++) {
        for (let x = 0; x < matrix.size; x++) {
          if (matrix.modules[y]![x]) {
            ctx.fillRect(left + (x + quiet) * scale, top + (y + quiet) * scale, scale, scale);
          }
        }
      }
    });

    this.y += side;
    return this;
  }

  finish(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = this.width;
    canvas.height = Math.ceil(this.y + PAD);

    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000';
    ctx.textBaseline = 'alphabetic';
    for (const op of this.ops) op(ctx);
    return canvas;
  }
}

/* ══ สลิปแต่ละแบบ ═════════════════════════════════════════════ */

function stamp(at: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(at.getDate())}/${p(at.getMonth() + 1)} ${p(at.getHours())}:${p(at.getMinutes())}`;
}

export interface KitchenSlipInput {
  kitchenName: string;
  /** ชื่อโต๊ะ หรือ "กลับบ้าน" / ชื่อเจ้าเดลิเวอรี่ */
  where: string;
  billNo: string;
  orderRound: number;
  items: Array<{ qty: number; name: string; note: string }>;
  paperWidthMm?: number;
  at?: Date;
}

/**
 * ใบครัว — ★ ไม่มีราคาเลยแม้แต่ตัวเดียว ★
 * ครัวต้องการรู้แค่ทำอะไรกี่จาน ราคาบนใบครัวมีแต่จะทำให้อ่านช้าลง
 *
 * พิมพ์เฉพาะรอบล่าสุด ถ้าพิมพ์ทั้งบิลทุกครั้งที่เพิ่มออเดอร์ ครัวจะทำซ้ำทันที
 */
export function renderKitchenSlip(input: KitchenSlipInput): HTMLCanvasElement {
  const slip = new Slip(dotsFor(input.paperWidthMm ?? 80));

  slip.text(input.where, { font: HEAD_FONT, align: 'center', lineHeight: 40 });
  slip.pair(stamp(input.at ?? new Date()), input.kitchenName, {
    font: META_FONT,
    lineHeight: 32,
  });
  slip.pair(input.billNo, input.orderRound > 1 ? `ออเดอร์เพิ่ม รอบ ${input.orderRound}` : '', {
    font: META_FONT,
    lineHeight: 32,
  });
  slip.rule();

  for (const item of input.items) {
    slip.item(item.qty, item.name, item.note, null);
    slip.rule();
  }
  return slip.finish();
}

export interface BillSlipInput {
  branch: { name: string; address: string; phone: string; promptpayId: string };
  billNo: string;
  receiptNo?: string | null;
  where: string;
  guestCount: number;
  businessDate: string;
  items: Array<{ qty: number; name: string; note: string; lineTotalSatang: number }>;
  subtotalSatang: number;
  discounts: Array<{ label: string; amountSatang: number }>;
  totalSatang: number;
  payments?: Array<{ name: string; amountSatang: number }>;
  changeSatang?: number;
  /** ใส่ QR พร้อมเพย์พร้อมยอด — ใช้กับใบแจ้งยอดก่อนชำระ */
  withQr: boolean;
  paperWidthMm?: number;
  at?: Date;
}

/**
 * ใบแจ้งยอด / ใบเสร็จ
 *
 * ★ QR พร้อมเพย์ระบุยอดของบิลใบนี้ไว้ในตัว ★
 * ลูกค้าสแกนแล้วยอดขึ้นเองเต็มจำนวน ไม่ต้องพิมพ์ตัวเลขเอง
 * ซึ่งเป็นจุดที่โอนผิดยอดบ่อยที่สุด และเป็นต้นเหตุที่พนักงานต้องนั่งเทียบสลิป
 */
export function renderBillSlip(input: BillSlipInput): HTMLCanvasElement {
  const slip = new Slip(dotsFor(input.paperWidthMm ?? 80));

  slip.text(input.branch.name, { font: HEAD_FONT, align: 'center', lineHeight: 40 });
  if (input.branch.address) {
    slip.text(input.branch.address, { font: SMALL_FONT, align: 'center', lineHeight: 28 });
  }
  if (input.branch.phone) {
    slip.text(`โทร ${input.branch.phone}`, { font: SMALL_FONT, align: 'center', lineHeight: 28 });
  }
  slip.gap(6).rule();

  slip.pair(input.receiptNo ? 'ใบเสร็จ' : 'ใบแจ้งยอด', input.receiptNo ?? input.billNo, {
    font: META_FONT,
    lineHeight: 32,
  });
  if (input.receiptNo) {
    slip.pair('เลขบิล', input.billNo, { font: SMALL_FONT, lineHeight: 28 });
  }
  slip.pair(stamp(input.at ?? new Date()), input.where, { font: META_FONT, lineHeight: 32 });
  if (input.guestCount > 0) {
    slip.pair('จำนวนลูกค้า', `${input.guestCount} คน`, { font: SMALL_FONT, lineHeight: 28 });
  }
  slip.rule();

  for (const item of input.items) {
    slip.item(item.qty, item.name, item.note, baht(item.lineTotalSatang));
  }
  slip.gap(4).rule();

  slip.pair('รวม', baht(input.subtotalSatang), { font: META_FONT });
  for (const discount of input.discounts) {
    slip.pair(discount.label, `-${baht(discount.amountSatang)}`, { font: META_FONT });
  }
  slip.rule('solid');
  slip.pair('ยอดสุทธิ', baht(input.totalSatang), { font: TOTAL_FONT, lineHeight: 44 });

  if (input.payments?.length) {
    slip.rule();
    for (const payment of input.payments) {
      slip.pair(payment.name, baht(payment.amountSatang), { font: META_FONT });
    }
    if (input.changeSatang) {
      slip.pair('เงินทอน', baht(input.changeSatang), { font: META_FONT });
    }
  }

  if (input.withQr && input.branch.promptpayId && input.totalSatang > 0) {
    slip.gap(10).rule();
    slip.gap(6).text('สแกนจ่ายด้วยพร้อมเพย์', {
      font: META_FONT,
      align: 'center',
      lineHeight: 32,
    });
    slip.gap(4).qr(promptPayPayload(input.branch.promptpayId, input.totalSatang), 340);
    slip.text(`ยอด ${baht(input.totalSatang)} บาท`, {
      font: META_FONT,
      align: 'center',
      lineHeight: 34,
    });
  }

  slip.gap(10).text('ขอบคุณที่ใช้บริการ', {
    font: SMALL_FONT,
    align: 'center',
    lineHeight: 30,
  });
  return slip.finish();
}

export interface DrawerSlipInput {
  branchName: string;
  docNo: string;
  businessDate: string;
  openingFloatSatang: number;
  expectedSatang: number;
  countedSatang: number;
  varianceSatang: number;
  keepSatang: number;
  paperWidthMm?: number;
  at?: Date;
}

/** ใบสรุปปิดรอบลิ้นชัก — เก็บไว้เป็นหลักฐานคู่กับซองเงินที่นำส่ง */
export function renderDrawerSlip(input: DrawerSlipInput): HTMLCanvasElement {
  const slip = new Slip(dotsFor(input.paperWidthMm ?? 80));

  slip.text('สรุปปิดรอบลิ้นชัก', { font: HEAD_FONT, align: 'center', lineHeight: 40 });
  slip.text(input.branchName, { font: META_FONT, align: 'center', lineHeight: 32 });
  slip.rule();

  slip.pair('เลขที่', input.docNo, { font: META_FONT });
  slip.pair('วันทำการ', input.businessDate, { font: META_FONT });
  slip.pair('ปิดรอบเมื่อ', stamp(input.at ?? new Date()), { font: META_FONT });
  slip.rule();

  slip.pair('เงินต้นรอบ', baht(input.openingFloatSatang), { font: META_FONT });
  slip.pair('เงินที่ควรมี', baht(input.expectedSatang), { font: META_FONT });
  slip.pair('นับได้จริง', baht(input.countedSatang), { font: META_FONT });
  slip.rule('solid');

  const variance = input.varianceSatang;
  slip.pair(
    variance === 0 ? 'ตรงพอดี' : variance > 0 ? 'เงินเกิน' : 'เงินขาด',
    variance === 0 ? '0.00' : baht(Math.abs(variance)),
    { font: TOTAL_FONT, lineHeight: 44 },
  );
  slip.rule();
  slip.pair('เก็บไว้ในลิ้นชัก', baht(input.keepSatang), { font: META_FONT });
  slip.pair('นำส่ง', baht(input.countedSatang - input.keepSatang), { font: META_FONT });

  slip.gap(24).pair('ผู้นับ', '____________________', { font: SMALL_FONT, lineHeight: 40 });
  slip.pair('ผู้รับ', '____________________', { font: SMALL_FONT, lineHeight: 40 });
  return slip.finish();
}

/** QR สำหรับแสดงบนจอให้ลูกค้าสแกน — คมชัดกว่าการให้ลูกค้าสแกนจากกระดาษ */
export function renderQrCanvas(text: string, targetPx: number): HTMLCanvasElement {
  const matrix = encodeQr(text);
  const quiet = 4;
  const scale = Math.max(2, Math.floor(targetPx / (matrix.size + quiet * 2)));
  const side = (matrix.size + quiet * 2) * scale;

  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;

  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, side, side);
  ctx.fillStyle = '#000';
  for (let y = 0; y < matrix.size; y++) {
    for (let x = 0; x < matrix.size; x++) {
      if (matrix.modules[y]![x]) {
        ctx.fillRect((x + quiet) * scale, (y + quiet) * scale, scale, scale);
      }
    }
  }
  return canvas;
}
